import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useId, useRef } from 'react';

import type { ColorName, TooltipPosition } from './buttonClasses';
import { cn } from '../cn';
import Button from './Button';

// ---------------------------------------------------------------------------
// Inline confirmation popover — a small "Are you sure?" card anchored NEXT TO
// the trigger (not a centered modal), so the Cancel/Confirm buttons land right
// under the pointer. Built on the same CSS Anchor Positioning + Popover API
// stack as FilterPopover/Tooltip: it renders in the browser top layer (escapes
// overflow/transform/z-index clipping) and `positionTryFallbacks` auto-flips it
// to stay on-screen — that's the "moves around so it doesn't get cropped"
// behaviour, for free and native.
//
// `popover="auto"` gives light-dismiss (click outside = cancel) and Escape =
// cancel with no extra wiring.
// ---------------------------------------------------------------------------

/** The visible/behavioural config of a confirmation — the object a caller passes to `Button`'s `confirm` prop. */
export type ConfirmConfig = {
  /** Heading of the confirmation. Defaults to `'Are you sure?'`. */
  title?: ReactNode;
  /** Optional explanatory line under the title. */
  description?: ReactNode;
  /** Label of the confirm (destructive) button. Defaults to `'Delete'`. */
  confirmLabel?: string;
  /** Label of the cancel button. Defaults to `'Cancel'`. */
  cancelLabel?: string;
  /** Color of the confirm button. Defaults to `'red'`. */
  confirmColor?: ColorName;
  /** Optional icon for the confirm button. */
  confirmIcon?: ReactNode;
  /**
   * Which side of the trigger the popover prefers. Defaults to `'bottom'`. It always flips to stay
   * on-screen. A span variant (e.g. `'top span-left'`) opens the card toward the trigger so the
   * confirm button lands under the pointer — handy for a top-right ✕, where `'top span-left'` pops the
   * card up-and-left with Confirm right by the ✕ (minimal mouse travel).
   */
  position?: TooltipPosition;
};

export type ConfirmPopoverProps = ConfirmConfig & {
  /** Must match the trigger's `popoverTarget`. */
  popoverId: string;
  /** The `--confirm-…` anchor name set on the trigger's `style.anchorName`. */
  anchorName: string;
  /** Ref to the popover element (so callers can `hidePopover()` imperatively). */
  popoverRef: RefObject<HTMLDivElement | null>;
  /** Fires when the user confirms. Receives the confirm button's click event. */
  onConfirm: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Fires when the popover is dismissed without confirming (Cancel / Escape / outside-click). */
  onCancel?: () => void;
};

/**
 * Positioning for the card. Prefers directly below the trigger, left-aligned and
 * spanning right, and flips on either axis near a viewport edge — mirrors
 * FilterPopover's proven collision handling. `position` picks the preferred block side.
 */
function confirmPopoverStyle(anchorName: string, position: TooltipPosition): CSSProperties {
  const main = position.split(' ')[0];
  const gap =
    main === 'top'
      ? { marginBottom: '6px' }
      : main === 'bottom'
        ? { marginTop: '6px' }
        : main === 'left'
          ? { marginRight: '6px' }
          : { marginLeft: '6px' };
  // A span variant (e.g. 'top span-left') passes the physical `position-area` straight through — mirrors
  // tooltipStyle — so the card opens toward the trigger (up-and-left off a top-right ✕), landing the
  // confirm button under the pointer. Plain 'top'/'bottom' keep the original left-aligned card that spans
  // right (justify-self start), so existing confirms are unchanged.
  if (position.includes('span')) {
    return {
      position: 'fixed',
      positionAnchor: anchorName,
      positionArea: position,
      positionTryFallbacks: 'flip-block, flip-inline, flip-block flip-inline',
      margin: 0,
      inset: 'auto',
      ...gap,
    } as unknown as CSSProperties;
  }
  const blockArea = main === 'top' ? 'block-start' : 'block-end';
  return {
    position: 'fixed',
    positionAnchor: anchorName,
    positionArea: `${blockArea} span-inline-end`,
    positionTryFallbacks: 'flip-block, flip-inline, flip-block flip-inline',
    justifySelf: 'start',
    margin: 0,
    inset: 'auto',
    ...gap,
  } as unknown as CSSProperties;
}

/** Generates the matched `popoverId` + `--confirm-…` anchor name + popover ref for a confirmation. */
export function useConfirmAnchor() {
  const id = useId();
  const clean = id.replace(/:/g, '');
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = `confirm-${clean}`;
  const anchorName = `--confirm-${clean}`;
  const anchorStyle = { anchorName } as CSSProperties;
  return { popoverId, anchorName, anchorStyle, popoverRef };
}

export default function ConfirmPopover({
  popoverId,
  anchorName,
  popoverRef,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  confirmColor = 'red',
  confirmIcon,
  position = 'bottom',
  onConfirm,
  onCancel,
}: ConfirmPopoverProps) {
  // Distinguishes a Confirm-click close (no cancel callback) from a
  // Cancel/Escape/outside-click close (fires onCancel).
  const didConfirm = useRef(false);

  const close = () => popoverRef.current?.hidePopover();

  return (
    <div
      ref={popoverRef}
      id={popoverId}
      popover="auto"
      role="dialog"
      aria-label={typeof title === 'string' ? title : 'Confirm'}
      onToggle={(e) => {
        const open = (e as React.ToggleEvent<HTMLDivElement>).newState === 'open';
        if (open) {
          didConfirm.current = false;
          // Focus Cancel by default so a stray Enter can't fire the destructive action.
          requestAnimationFrame(() =>
            popoverRef.current?.querySelector<HTMLButtonElement>('[data-confirm-cancel]')?.focus(),
          );
          return;
        }
        if (didConfirm.current) {
          didConfirm.current = false;
          return;
        }
        onCancel?.();
      }}
      style={confirmPopoverStyle(anchorName, position)}
      className={cn(
        'w-64 max-w-[calc(100vw-1rem)] rounded-lg border p-3 text-left shadow-xl',
        'border-gray-200 bg-white text-gray-900',
        'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100',
      )}>
      {title != null && title !== '' && <p className="text-sm font-semibold">{title}</p>}
      {description != null && description !== '' && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" data-confirm-cancel onClick={close}>
          {cancelLabel}
        </Button>
        <Button
          size="sm"
          color={confirmColor}
          icon={confirmIcon}
          onClick={(e) => {
            didConfirm.current = true;
            close();
            onConfirm(e);
          }}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
